<template>
  <v-dialog v-model="internalValue" max-width="800" persistent scrollable>
    <v-card>
      <v-card-title class="headline primary white--text">
        {{ isEditing ? "Editar Perfil de Acceso" : "Nuevo Perfil de Acceso" }}
        <v-spacer></v-spacer>
        <v-btn icon dark @click="closeModal"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>

      <v-card-text class="pa-4" style="height: 60vh;">
        <v-form ref="roleForm" v-model="isFormValid">
          <div class="mb-6">
            <div class="text-subtitle-1 font-weight-medium mb-1">
              ¿Para qué rol de usuario es este perfil?
            </div>
            <p class="text-caption grey--text mb-2">
              El rol principal del usuario no cambia. Define si este perfil
              aplica a usuarios Admin o Broker. Las reglas de precio
              seleccionadas serán visibles para este perfil.
            </p>
            <div class="mt-2">
              <v-chip
                class="mr-2 mb-2"
                :color="selectedBaseRole === 'admin' ? 'blue darken-1' : ''"
                :outlined="selectedBaseRole !== 'admin'"
                :dark="selectedBaseRole === 'admin'"
                @click="subroleForm.baseRole = 'admin'"
              >
                <v-icon left small>mdi-shield-account</v-icon>
                Usuarios Admin
              </v-chip>
              <v-chip
                class="mb-2"
                :color="selectedBaseRole === 'broker' ? 'blue darken-1' : ''"
                :outlined="selectedBaseRole !== 'broker'"
                :dark="selectedBaseRole === 'broker'"
                @click="subroleForm.baseRole = 'broker'"
              >
                <v-icon left small>mdi-briefcase-account</v-icon>
                Usuarios Broker
              </v-chip>
            </div>
          </div>

          <v-row>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="subroleForm.name"
                label="Nombre *"
                outlined
                dense
                @input="generateSlug"
                :rules="[v => !!v || 'Requerido']"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="subroleForm.slug"
                label="Slug"
                outlined
                dense
                disabled
                hint="Autogenerado"
                persistent-hint
              ></v-text-field>
            </v-col>
            <v-col cols="12">
              <v-textarea
                v-model="subroleForm.description"
                label="Descripción"
                outlined
                dense
                rows="2"
              ></v-textarea>
            </v-col>
          </v-row>

          <v-divider class="my-4"></v-divider>

          <h3 class="text-h6 mb-3">Acceso a reglas de precio</h3>
          <p class="text-caption grey--text mb-4">
            Este perfil define qué reglas de precio podrá ver el usuario en
            cotizaciones. Su rol principal no cambia.
          </p>

          <v-expansion-panels v-model="expandedPanels">
            <v-expansion-panel>
              <v-expansion-panel-header class="font-weight-bold">
                <v-icon left color="primary" class="mr-2"
                  >mdi-cash-multiple</v-icon
                >
                Reglas de precio (cotizador)
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-checkbox
                  v-model="subroleForm.policies.pricing_rules.allowAll"
                  label="Permitir todas las reglas"
                  color="primary"
                  hide-details
                  class="mb-3 mt-0"
                />
                <v-select
                  v-if="!subroleForm.policies.pricing_rules.allowAll"
                  v-model="subroleForm.policies.pricing_rules.allowedIds"
                  :items="pricingRulesAvailable"
                  item-text="name"
                  item-value="id"
                  label="Reglas permitidas"
                  multiple
                  chips
                  deletable-chips
                  :loading="pricingRulesLoading"
                  outlined
                  dense
                ></v-select>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-form>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="pa-4 bg-light-grey">
        <v-spacer></v-spacer>
        <v-btn text @click="closeModal">Cancelar</v-btn>
        <v-btn color="primary" @click="saveRole" :disabled="!isFormValid">
          <v-icon left>mdi-content-save</v-icon>
          Guardar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { CreateSubrole, UpdateSubrole } from "@/api/subrole-api";
import { ListRulesPricing } from "@/api/real-estate-pricing-rules-api";

export default {
  name: "RoleSetupModal",
  props: {
    value: { type: Boolean, default: false },
    roleData: { type: Object, default: () => null }
  },
  data() {
    return {
      isFormValid: false,
      expandedPanels: 0,
      pricingRulesAvailable: [],
      pricingRulesLoading: false,
      // state del subrol
      subroleForm: {
        name: "",
        slug: "",
        description: "",
        baseRole: "broker",
        policies: {
          pricing_rules: {
            allowAll: true,
            allowedIds: []
          }
        }
      }
    };
  },
  computed: {
    selectedBaseRole() {
      return this.subroleForm.baseRole;
    },
    internalValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      }
    },
    isEditing() {
      return !!this.roleData;
    }
  },
  watch: {
    value(isOpen) {
      if (isOpen) {
        this.fetchPricingRules();
        if (this.roleData) {
          this.subroleForm = this.setSubroleForm(this.roleData);
        }
      }
    }
  },
  methods: {
    async fetchPricingRules() {
      this.pricingRulesLoading = true;
      try {
        const res = await ListRulesPricing();
        const rules = res?.PricingRules || [];
        this.pricingRulesAvailable = rules
          .filter(r => r.status == 1)
          .map(r => ({
            id: r._id,
            name: r.description
          }));
      } catch (err) {
        this.$snotify.error("Error al cargar reglas de precio.", "Error");
        this.pricingRulesAvailable = [];
      } finally {
        this.pricingRulesLoading = false;
      }
    },

    setSubroleForm(roleData) {
      return {
        _id: roleData._id,
        name: roleData.name,
        slug: roleData.slug,
        description: roleData.description,
        baseRole: roleData.baseRole,
        policies: roleData.policies
      };
    },

    generateSlug() {
      if (!this.subroleForm.name) {
        this.subroleForm.slug = "";
        return;
      }
      this.subroleForm.slug = this.subroleForm.name
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)+/g, "");
    },

    saveRole() {
      if (!this.$refs.roleForm.validate()) return;

      const payload = {
        ...this.subroleForm
      };

      if (this.isEditing) {
        payload.subroleId = this.roleData._id;
        payload.baseRole = this.roleData.baseRole;
        UpdateSubrole({
          ...payload
        })
          .then(res => {
            this.$snotify.success(
              "Subrol actualizado correctamente",
              "Completado"
            );
            this.$emit("save", res.subrole);
            this.closeModal();
          })
          .catch(err => {
            this.$snotify.error(
              "Error al actualizar el subrol: " + (err.message || err),
              "Error"
            );
          });
      } else {
        CreateSubrole({ ...payload })
          .then(res => {
            this.$snotify.success("Subrol creado correctamente", "Completado");
            this.$emit("save", res.subrole);
            this.closeModal();
          })
          .catch(err => {
            this.$snotify.error(
              "Error al crear el subrol: " + (err.message || err),
              "Error"
            );
          });
      }
    },

    closeModal() {
      this.internalValue = false;
    }
  }
};
</script>

<style scoped>
.bg-light-grey {
  background-color: #f5f5f5;
}
</style>
